// Touchpoints live under the Growth nav group, not Risk: they are
// contact-keyed engagement records with no risk relationship, and the
// backend's authz taxonomy already files them under the "growth" write
// area (matt-issue 17). The tab components stay in Risk/ until the
// item-18 branch lands to avoid a directory-move collision. Growth
// opportunities are surfaced here (not on Risk) to match the mockup's
// Touchpoints screen: Customer + Growth subtabs.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraEmptyState, SpecraTabBar, SpecraTabPanel, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Handshake } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageBody } from '../../../../components/layout';
import { useProgramContext } from '../../../../layouts/ProgramLayout';
import { programRef } from '../../../../lib/access/types';
import { useView } from '../../../../lib/access/useView';
import { CustomerTouchpointsTab } from '../../Risk/CustomerTouchpointsTab';
import { GrowthOpportunitiesTab } from '../../Risk/GrowthOpportunitiesTab';
export function TouchpointsPage() {
    const { programId, program } = useProgramContext();
    const [active, setActive] = useState('customer');
    // REG-11: ?logTouchpoint=1 (from a registry Person page) opens the create
    // form once; the params are dropped after use so Back/reload do not reopen it.
    const [searchParams, setSearchParams] = useSearchParams();
    const requestCreate = searchParams.get('logTouchpoint') === '1';
    const clearLogParams = useCallback(()=>setSearchParams((prev)=>{
            const next = new URLSearchParams(prev);
            next.delete('logTouchpoint');
            next.delete('contact');
            return next;
        }, {
            replace: true
        }), [
        setSearchParams
    ]);
    // Both tabs are Growth data, read only with area:growth on the program.
    const programView = useView(programRef(programId));
    if (programView.isLoading) return null;
    if (!programView.granted('area:growth')) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                "data-testid": Testids.deliveryTouchpoints.noGrowthAccess,
                description: "Customer touchpoints and growth opportunities are Growth information. Ask an admin for Growth access on this program.",
                framed: true,
                icon: /*#__PURE__*/ _jsx(Handshake, {}),
                title: "No Growth access"
            })
        });
    }
    return /*#__PURE__*/ _jsx(PageBody, {
        children: /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsxs(SpecraText, {
                    children: [
                        "Program: ",
                        program?.programNameShort || program?.programNameFull || programId
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraTabBar, {
                    idBase: "touchpoints",
                    value: active,
                    onValueChange: setActive,
                    options: [
                        {
                            value: 'customer',
                            label: 'Touchpoints',
                            testId: Testids.deliveryTouchpoints.subTabCustomer
                        },
                        {
                            value: 'growth',
                            label: 'Growth Opportunities',
                            testId: Testids.deliveryTouchpoints.subTabGrowth
                        }
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraTabPanel, {
                    active: active === 'customer',
                    idBase: "touchpoints",
                    value: "customer",
                    className: "mt-6",
                    children: /*#__PURE__*/ _jsx(CustomerTouchpointsTab, {
                        programId: programId,
                        requestCreate: requestCreate,
                        onCreateRequestHandled: clearLogParams
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraTabPanel, {
                    active: active === 'growth',
                    idBase: "touchpoints",
                    value: "growth",
                    className: "mt-6",
                    children: /*#__PURE__*/ _jsx(GrowthOpportunitiesTab, {
                        programId: programId
                    })
                })
            ]
        })
    });
}
